// OFF-C-01: what the Seller's screens last read (their truck stock, prices, customers and invoices, products) is kept on
// the phone, so the app opens and sells without signal. Only the Seller's phone keeps it (the owner's figures carry cost
// and cash and stay on the server). IndexedDB stores the data as it is (Maps included); it is cleared at sign-out.
import { dehydrate, hydrate, type QueryClient } from '@tanstack/react-query';

const DB = 'yakvender-cache';
const STORE = 'kv';
/** Reads the Seller's screens depend on; reports, settings and the owner's data are never kept. */
const KEEP = new Set(['catalog', 'stock', 'rounds', 'sales', 'pre-orders']);

function open(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB, 1);
    req.onupgradeneeded = () => req.result.createObjectStore(STORE);
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}
async function kv<T>(mode: IDBTransactionMode, fn: (s: IDBObjectStore) => IDBRequest<T>): Promise<T> {
  const db = await open();
  return new Promise<T>((resolve, reject) => {
    const req = fn(db.transaction(STORE, mode).objectStore(STORE));
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

/** Loads the last cache into the query client (once), then keeps it up to date while the Seller works. Returns the stop. */
export function keepSellerCache(qc: QueryClient, userId: string): () => void {
  if (typeof indexedDB === 'undefined') return () => {};
  const key = `cache:${userId}`;
  let timer: ReturnType<typeof setTimeout> | undefined;
  let stopped = false;
  void kv('readonly', (s) => s.get(key))
    .then((saved) => {
      if (saved && !stopped) hydrate(qc, saved);
    })
    .catch(() => {});
  const unsubscribe = qc.getQueryCache().subscribe(() => {
    clearTimeout(timer);
    timer = setTimeout(() => {
      const state = dehydrate(qc, {
        shouldDehydrateQuery: (q) => q.state.status === 'success' && KEEP.has(String(q.queryKey[0])),
      });
      void kv('readwrite', (s) => s.put(state, key)).catch(() => {});
    }, 2000);
  });
  return () => {
    stopped = true;
    clearTimeout(timer);
    unsubscribe();
  };
}

/** Sign-out: nothing of the Seller stays on a phone they hand over. */
export async function clearCaches(): Promise<void> {
  if (typeof indexedDB === 'undefined') return;
  try {
    await kv('readwrite', (s) => s.clear());
  } catch {
    /* nothing to clear */
  }
}
